import React, { useDeferredValue, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaEdit, FaEye, FaPlus, FaTrashAlt } from "react-icons/fa";
import adminApi from "services/admin-api";
import { EmptyState, SectionCard, StatusPill } from "components/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import RightDrawer from "components/global/RightDrawer";
import { PageHeader } from "components/admin/ui";
import { SportForm } from "components/molecules/forms/sport-form/sport-form";
import { getSportsWorkspaceContent } from "pages/app/sports/sports-access";
import { getSportSpotlightItems } from "pages/app/sports/sports-spotlight";
import { isRecoverableUploadResponseError } from "utils/admin";
import { useAuth } from "context/auth-context";
import { hasPortalAction } from "utils/portal-access";
import { getSportVisual } from "utils/sport-icons";
import {
  SportsPrimaryButton,
  SportsCardActionRow,
  SportsCardActionsGroup,
  SportsCardGrid,
  SportsCardHeader,
  SportsCardIconButton,
  SportsCardIdentity,
  SportsCardOpenButton,
  SportsCardSkeleton,
  SportsCardSkeletonBlock,
  SportsCardSkeletonGrid,
  SportsCardStatusWrap,
  SportsCardSubtext,
  SportsListingCard,
  SportsFeaturedBadge,
  SportsFeaturedCard,
  SportsFeaturedGrid,
  SportsFeaturedMeta,
  SportsFeaturedSectionWrap,
  SportsFeaturedTitle,
  SportsInfoRow,
  SportsIconBadge,
  SportsPaginationBar,
  SportsPaginationButton,
  SportsPaginationControls,
  SportsPaginationLabel,
  SportsPaginationText,
  SportsSectionToolbar,
  SportsThumbnailFrame,
  SportsThumbnailImage,
  SportsFieldInput,
  SportsLabelText,
  SportsMetaText,
  SportsFieldSelect,
} from "pages/app/sports/styles/sports.page.inline.styles";
const SportsPage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { portalAccess } = useAuth();
  const [searchValue, setSearchValue] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sportsPage, setSportsPage] = useState(1);
  const [selectedSport, setSelectedSport] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const deferredSearch = useDeferredValue(searchValue);
  const sportsPageSize = 10;
  const { data: sports = [], isLoading } = useQuery({
    queryKey: ["sports"],
    queryFn: () => adminApi.sports.list(),
  });
  const saveMutation = useMutation({
    mutationFn: (payload: any) => {
      const normalizedPayload = {
        ...payload,
        founded_year: payload.founded_year ? Number(payload.founded_year) : undefined,
        popularity_rank: payload.popularity_rank ? Number(payload.popularity_rank) : undefined,
      };
      return selectedSport
        ? adminApi.sports.update(selectedSport.id, normalizedPayload)
        : adminApi.sports.create(normalizedPayload);
    },
    onSuccess: () => {
      toast.success(selectedSport ? "Sport updated." : "Sport created.");
      queryClient.invalidateQueries({
        queryKey: ["sports"],
      });
      setDrawerOpen(false);
      setSelectedSport(null);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Sport saved and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["sports"],
        });
        setDrawerOpen(false);
        setSelectedSport(null);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to save this sport.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.sports.remove(id),
    onSuccess: () => {
      toast.success("Sport deleted.");
      queryClient.invalidateQueries({
        queryKey: ["sports"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to delete this sport.");
    },
  });
  const filteredSports = sports.filter((sport: any) => {
    const matchesSearch = [sport.name, sport.region, sport.level]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const matchesRegion = regionFilter === "all" || sport.region === regionFilter;
    const matchesStatus = statusFilter === "all" || sport.status === statusFilter;
    return matchesSearch && matchesRegion && matchesStatus;
  });
  const totalSportsPages = Math.max(Math.ceil(filteredSports.length / sportsPageSize), 1);
  const paginatedSports = filteredSports.slice(
    (sportsPage - 1) * sportsPageSize,
    sportsPage * sportsPageSize,
  );
  const regions = Array.from(
    new Set(sports.map((sport: any) => sport.region).filter(Boolean)),
  ) as string[];
  const spotlightSports = getSportSpotlightItems(sports);
  const canManageSports = hasPortalAction(portalAccess, "sports.manage");
  const workspaceContent = getSportsWorkspaceContent(portalAccess, canManageSports);

  useEffect(() => {
    setSportsPage(1);
  }, [deferredSearch, regionFilter, statusFilter]);

  useEffect(() => {
    if (sportsPage > totalSportsPages) {
      setSportsPage(totalSportsPages);
    }
  }, [sportsPage, totalSportsPages]);

  return (
    <AppLayout route="/sports">
      <PageHeader
        title="Sports"
        description={workspaceContent.pageDescription}
        actions={
          canManageSports ? (
            <SportsPrimaryButton
              onClick={() => {
                setSelectedSport(null);
                setDrawerOpen(true);
              }}
              type="button"
            >
              <FaPlus size={12} />
              Add sport
            </SportsPrimaryButton>
          ) : null
        }
      />

      {workspaceContent.showFeaturedSection ? (
        <SportsFeaturedSectionWrap>
          <SectionCard
            subtitle="Featured from live participation signals and catalogue ranking, not page-view guesses."
            title="Platform Spotlight"
          >
            {spotlightSports.length ? (
              <SportsFeaturedGrid>
                {spotlightSports.map((sport: any) => {
                  const sportVisual = getSportVisual(sport.name);

                  return (
                    <SportsFeaturedCard
                      key={sport.id}
                      onClick={() => history.push(`/sports/${sport.id}`)}
                      type="button"
                    >
                      <SportsInfoRow>
                        <SportsThumbnailFrame>
                          <SportsThumbnailImage
                            alt={sport.name || "Sport"}
                            fallbackLabel={sport.name}
                            src={sport.image_url}
                            variant="sport"
                          />
                          <SportsIconBadge
                            $accent={sportVisual.accent}
                            $glow={sportVisual.glow}
                            aria-hidden="true"
                        >
                          <sportVisual.Icon size={12} />
                        </SportsIconBadge>
                      </SportsThumbnailFrame>
                      <div>
                        <SportsFeaturedTitle>{sport.name}</SportsFeaturedTitle>
                        <SportsMetaText>
                          {sport.spotlight_reason || sport.region || "Sport spotlight"}
                        </SportsMetaText>
                      </div>
                    </SportsInfoRow>
                    <SportsFeaturedMeta>
                      <span>{sport.athletes_count ?? 0} athletes</span>
                      <span>{sport.teams_count ?? 0} teams</span>
                    </SportsFeaturedMeta>
                    <SportsFeaturedBadge>{sport.spotlight_label || "Spotlight"}</SportsFeaturedBadge>
                  </SportsFeaturedCard>
                );
              })}
            </SportsFeaturedGrid>
          ) : (
            <EmptyState
              description="Spotlight cards appear when a sport has ranking or participation signals such as active teams, linked athletes, or an explicit catalogue rank."
              title="No spotlight signals yet"
            />
          )}
        </SectionCard>
        </SportsFeaturedSectionWrap>
      ) : null}

      <SectionCard
        action={
          <SportsSectionToolbar>
            <SportsFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search sports by name, region, or level"
              value={searchValue}
            />
            <SportsFieldSelect
              onChange={(event) => setRegionFilter(event.target.value)}
              value={regionFilter}
            >
              <option value="all">All regions</option>
              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </SportsFieldSelect>
            <SportsFieldSelect
              onChange={(event) => setStatusFilter(event.target.value)}
              value={statusFilter}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </SportsFieldSelect>
          </SportsSectionToolbar>
        }
        subtitle={workspaceContent.listSubtitle}
        title={workspaceContent.listTitle}
      >
        {isLoading ? (
          <SportsCardSkeletonGrid>
            {Array.from({ length: 6 }).map((_, index) => (
              <SportsCardSkeleton key={`sport-skeleton-${index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <SportsCardSkeletonBlock className="h-12 w-12" />
                    <div className="space-y-2">
                      <SportsCardSkeletonBlock className="h-4 w-28" />
                      <SportsCardSkeletonBlock className="h-3 w-20" />
                    </div>
                  </div>
                  <SportsCardSkeletonBlock className="h-7 w-16 rounded-full" />
                </div>
                <div className="mt-4 space-y-2">
                  <SportsCardSkeletonBlock className="h-3 w-full" />
                  <SportsCardSkeletonBlock className="h-3 w-4/5" />
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {Array.from({ length: 4 }).map((__, metaIndex) => (
                    <SportsCardSkeletonBlock
                      key={`sport-skeleton-meta-${index}-${metaIndex}`}
                      className="h-16"
                    />
                  ))}
                </div>
                <div className="mt-4 flex gap-2">
                  <SportsCardSkeletonBlock className="h-10 w-28" />
                  <SportsCardSkeletonBlock className="h-10 w-24" />
                </div>
              </SportsCardSkeleton>
            ))}
          </SportsCardSkeletonGrid>
        ) : filteredSports.length ? (
          <>
            <SportsCardGrid>
              {paginatedSports.map((sport: any) => {
              const sportVisual = getSportVisual(sport.name);

              return (
                <SportsListingCard key={sport.id}>
                  <SportsCardHeader>
                    <SportsInfoRow>
                      <SportsThumbnailFrame>
                        <SportsThumbnailImage
                          alt={sport.name || "Sport"}
                          fallbackLabel={sport.name}
                          src={sport.image_url}
                          variant="sport"
                        />
                        <SportsIconBadge
                          $accent={sportVisual.accent}
                          $glow={sportVisual.glow}
                          aria-hidden="true"
                        >
                          <sportVisual.Icon size={12} />
                        </SportsIconBadge>
                      </SportsThumbnailFrame>
                      <SportsCardIdentity>
                        <SportsLabelText>{sport.name}</SportsLabelText>
                        <SportsCardSubtext>Sport profile</SportsCardSubtext>
                      </SportsCardIdentity>
                    </SportsInfoRow>
                    <SportsCardStatusWrap>
                      <StatusPill value={sport.status} />
                    </SportsCardStatusWrap>
                  </SportsCardHeader>

                  <SportsCardActionRow>
                    <SportsCardOpenButton
                      onClick={() => history.push(`/sports/${sport.id}`)}
                      type="button"
                    >
                      Open
                    </SportsCardOpenButton>
                    <SportsCardActionsGroup>
                      <SportsCardIconButton
                        aria-label={`View ${sport.name}`}
                        onClick={() => history.push(`/sports/${sport.id}`)}
                        title="View sport"
                        type="button"
                      >
                        <FaEye size={12} />
                      </SportsCardIconButton>
                      {canManageSports ? (
                        <SportsCardIconButton
                          aria-label={`Edit ${sport.name}`}
                          onClick={() => {
                            setSelectedSport(sport);
                            setDrawerOpen(true);
                          }}
                          title="Edit sport"
                          type="button"
                        >
                          <FaEdit size={12} />
                        </SportsCardIconButton>
                      ) : null}
                      {canManageSports ? (
                        <SportsCardIconButton
                          aria-label={`Delete ${sport.name}`}
                          onClick={() => {
                            if (
                              window.confirm("Delete this sport? This action cannot be undone.")
                            ) {
                              deleteMutation.mutate(String(sport.id));
                            }
                          }}
                          title="Delete sport"
                          type="button"
                        >
                          <FaTrashAlt size={12} />
                        </SportsCardIconButton>
                      ) : null}
                    </SportsCardActionsGroup>
                  </SportsCardActionRow>
                </SportsListingCard>
              );
            })}
            </SportsCardGrid>

            <SportsPaginationBar>
              <SportsPaginationText>
                Showing {(sportsPage - 1) * sportsPageSize + 1} to{" "}
                {Math.min(sportsPage * sportsPageSize, filteredSports.length)} of{" "}
                {filteredSports.length} sports
              </SportsPaginationText>
              <SportsPaginationControls>
                <SportsPaginationButton
                  disabled={sportsPage === 1}
                  onClick={() => setSportsPage((currentPage) => currentPage - 1)}
                  type="button"
                >
                  Previous
                </SportsPaginationButton>
                <SportsPaginationLabel>
                  Page {sportsPage} of {totalSportsPages}
                </SportsPaginationLabel>
                <SportsPaginationButton
                  disabled={sportsPage >= totalSportsPages}
                  onClick={() => setSportsPage((currentPage) => currentPage + 1)}
                  type="button"
                >
                  Next
                </SportsPaginationButton>
              </SportsPaginationControls>
            </SportsPaginationBar>
          </>
        ) : (
          <EmptyState
            description={workspaceContent.emptyDescription}
            title={workspaceContent.emptyTitle}
          />
        )}
      </SectionCard>

      {canManageSports ? (
        <RightDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedSport(null);
          }}
          title={selectedSport ? "Edit sport" : "Create sport"}
        >
          <SportForm
            initialValues={{
              name: selectedSport?.name || "",
              region: selectedSport?.region || "",
              level: selectedSport?.level || "",
              status: selectedSport?.status || "active",
              founded_year: selectedSport?.founded_year || null,
              popularity_rank: selectedSport?.popularity_rank || null,
              image: selectedSport?.image_url || "",
            }}
            onCancel={() => {
              setDrawerOpen(false);
              setSelectedSport(null);
            }}
            onSubmit={(values) => saveMutation.mutate(values)}
          />
        </RightDrawer>
      ) : null}
    </AppLayout>
  );
};
export default SportsPage;
